/**
 * System Recovery Layer (client-side only)
 * ────────────────────────────────────────────────────────────────────────
 * A tiny, additive helper used by the System Status panel and the
 * User-Connection / Invite recovery banners to safely re-fetch app state
 * when a non-critical subsystem fails.
 *
 * SAFETY GUARANTEES (read carefully — do not relax these):
 *   • Never deletes, resets, or overwrites user data.
 *   • Never modifies billing, Stripe, subscriptions, auth accounts,
 *     family tree relationships, media files, invites, or Resend config.
 *   • Never creates duplicate profiles or duplicate family trees.
 *   • Never introduces new DB tables, columns, or RLS policies.
 *   • Only signals existing refresh paths the app already exposes:
 *       - dispatches `kc:family-graph-refresh` (already handled by
 *         FamilyGraphContext to re-resolve owner + re-fetch members)
 *       - calls a caller-supplied `refreshProfile()` (AuthContext)
 *       - resolves; the consumer decides what to do with the result.
 *   • Logs only via console.warn on retry failure — no analytics,
 *     no backend logging, no DB writes.
 *
 * Auto-retry protection:
 *   A given `systemId` can only auto-retry ONCE per page load
 *   (tracked in sessionStorage). The user can still tap a manual
 *   "Retry" button as many times as they like.
 */

const AUTO_RETRY_KEY_PREFIX = 'kc:recovery:auto-retried:';

/** Has this system already auto-retried during this page load? */
export function hasAutoRetried(systemId: string): boolean {
  try {
    return sessionStorage.getItem(AUTO_RETRY_KEY_PREFIX + systemId) === '1';
  } catch {
    return false;
  }
}

/** Mark that this system has consumed its single automatic retry. */
export function markAutoRetried(systemId: string): void {
  try {
    sessionStorage.setItem(AUTO_RETRY_KEY_PREFIX + systemId, '1');
  } catch {
    /* sessionStorage unavailable — fine, manual retries still work */
  }
}

export interface RecoveryOptions {
  /**
   * Optional caller-supplied profile refresher (typically
   * `useAuth().refreshProfile`). Awaited if provided. Failures are
   * swallowed with console.warn so one failing system never blocks
   * the rest.
   */
  refreshProfile?: () => Promise<void>;
  /**
   * Whether to fire the `kc:family-graph-refresh` window event so
   * any mounted FamilyGraphProvider re-fetches members. Default: true.
   */
  refreshFamilyGraph?: boolean;
  /**
   * Label used in console.warn output if any step fails. Helps
   * distinguish soft auto-retries from manual retries in the console.
   */
  source?: string;
}

/**
 * Run a single safe recovery pass. Returns true if every step we
 * attempted resolved without throwing, false otherwise. The caller
 * is responsible for showing UI loading / success state.
 *
 * This function NEVER throws — failures are caught and warned.
 */
export async function runSystemRecovery(
  opts: RecoveryOptions = {}
): Promise<boolean> {
  const { refreshProfile, refreshFamilyGraph = true, source = 'recovery' } = opts;
  let allOk = true;

  // 1) Ask AuthContext to re-pull the current user's profile row.
  //    This is the existing `fetchProfile(user.id, user.email)` path —
  //    it never creates duplicates and never overwrites claimed data.
  if (refreshProfile) {
    try {
      await refreshProfile();
    } catch (err) {
      allOk = false;
      console.warn(`[${source}] refreshProfile retry failed`, err);
    }
  }

  // 2) Tell every mounted FamilyGraphProvider to re-resolve its
  //    effective owner and re-fetch family_graph_members. This is the
  //    same event RelationshipOnboardingModal and ConnectUserModal
  //    already use after successful binds — it does not mutate data.
  if (refreshFamilyGraph) {
    try {
      window.dispatchEvent(new CustomEvent('kc:family-graph-refresh'));
    } catch (err) {
      allOk = false;
      console.warn(`[${source}] family-graph refresh dispatch failed`, err);
    }
  }

  return allOk;
}

/**
 * Convenience wrapper: run an automatic soft recovery for a specific
 * system, but ONLY if it hasn't already auto-retried during this
 * page load. Always safe to call. Returns:
 *   - 'skipped'  — auto-retry budget already used this page load
 *   - 'ok'       — recovery ran and every step succeeded
 *   - 'failed'   — recovery ran but at least one step warned
 */
export async function softAutoRetry(
  systemId: string,
  opts: RecoveryOptions = {}
): Promise<'skipped' | 'ok' | 'failed'> {
  if (hasAutoRetried(systemId)) return 'skipped';
  markAutoRetried(systemId);
  const ok = await runSystemRecovery({ ...opts, source: `auto:${systemId}` });
  return ok ? 'ok' : 'failed';
}
